import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ConfirmModal from "../form-components/ConfirmModal";
import { useLocalization } from "../../context/LocalizationContext";
import { useToast } from "../../context/ToastContext";
import LocalText from "../common/LocalText";
import { useLocalText } from "../common/useLocalText";
import { submitApproval } from "../../api/approvalApi";
import ApprovalStatusBadge from "../approval/ApprovalStatusBadge";
import SubmitForApprovalButton from "../approval/SubmitForApprovalButton";
import WithdrawButton from "../approval/WithdrawButton";
import ContentPreviewDrawer from "../approval/ContentPreviewDrawer";
import { CONTENT_TYPES } from '../../constants/contentTypes';

const MEDIA_ICONS = {
  1: <ImageIcon sx={{ fontSize: 38, color: "primary.lighter" }} />,
  2: <VideocamIcon sx={{ fontSize: 38, color: "primary.lighter" }} />,
  3: <MusicNoteIcon sx={{ fontSize: 38, color: "primary.lighter" }} />,
};

// Per-itemType display config — the single place that knows how "portal" / "media" / "custom"
// differ, so the render body below reads cfg.* instead of scattering mediaOnly ternaries.
// Adding a 4th future option is one new entry here, not a new ternary sprinkled through the file.
const ITEM_TYPE_CONFIG = {
  portal: {
    chipLabel: 'PORTAL',
    chipColor: 'primary.light',
    contentType: CONTENT_TYPES.PORTAL,
    entityText: 'portal',
  },
  media: {
    chipLabel: 'MEDIA',
    chipColor: 'secondary.main',
    contentType: CONTENT_TYPES.BOOTH_MEDIA,
    entityText: 'media',
  },
  custom: {
    chipLabel: 'CUSTOM',
    chipColor: '#7c3aed', // violet — distinct from portal's blue and media's secondary color
    contentType: CONTENT_TYPES.CUSTOM_BOOTH_ITEM,
    entityText: 'item',
  },
};

const getHostname = (url) => {
  try { return new URL(url).hostname; } catch { return null; }
};

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const PortalCard = ({ item, itemType = "portal", deletePortal, onEdit, onView, onViewLive, onSuccess, onEditRejected, onConvert, preview = false, parentLocked = false, showActions = true }) => {
  const { toast } = useToast();
  const [deleteModalOpen, setDeleteModalOpen]         = useState(false);
  const [proposeDeleteOpen, setProposeDeleteOpen]     = useState(false);
  const [imageError, setImageError]                   = useState(false);
  const [imageLoaded, setImageLoaded]                 = useState(false);
  const [hovered, setHovered]                         = useState(false);
  const [viewProposalOpen, setViewProposalOpen]       = useState(false);
  const { locale, t } = useLocalization();

  const cfg = ITEM_TYPE_CONFIG[itemType] ?? ITEM_TYPE_CONFIG.portal;
  const mediaOnly  = itemType === "media";
  const isCustom   = itemType === CONTENT_TYPES.CUSTOM;
  // Custom items span multiple options (today: Calendar) — the option name on the badge
  // ("CALENDAR") tells the user what the card actually is; the generic "CUSTOM" label
  // wouldn't. Falls back to cfg.chipLabel if optionType is ever missing.
  const badgeLabel = isCustom && item.optionType ? item.optionType.toUpperCase() : cfg.chipLabel;
  const mediaTypeId = item.correspondingMedia?.mediaTypeId || item.mediaTypeId || 1;

  const thumbLink =
    item.correspondingMedia?.linkLocalizations?.find((m) => m.localeId === locale)?.mediaLink ||
    item.correspondingMedia?.linkLocalizations?.[0]?.mediaLink ||
    null;

  const mediaDirectLink =
    mediaOnly
      ? (item.linkLocalizations?.find((m) => m.localeId === locale)?.mediaLink ||
         item.linkLocalizations?.[0]?.mediaLink ||
         null)
      : null;

  const previewSrc = mediaOnly ? (mediaTypeId === 1 ? mediaDirectLink : null) : thumbLink;
  const showThumb  = !!previewSrc && !imageError;

  // Subtitle link shown under the title: portal's first link, media's own direct link,
  // custom item's own external link (never another item's data).
  const subtitleLink = mediaOnly
    ? mediaDirectLink
    : isCustom
      ? item.links?.[0]?.externalLink
      : item.portalLinks?.[0]?.externalLink;
  const hostname = getHostname(subtitleLink);

  const title =
    item.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
    item.localizedPairs?.values?.[0]?.value ||
    t(item.localizedPairs?.key, '') ||
    '';
  const mediaTitle =
    item.textLocalizations?.find((l) => l.localeId === locale)?.value ||
    item.textLocalizations?.[0]?.value ||
    t(item.textKey, "") ||
    "";
  const displayName = mediaOnly ? mediaTitle : title;

  const deleteTitleText  = useLocalText(`Delete ${cfg.chipLabel.charAt(0)}${cfg.chipLabel.slice(1).toLowerCase()}`);
  const deleteEntityText = cfg.entityText;
  const isSnapshot       = item._viewType === 'snapshot';
  const isLocked         = item.approvalStatus === 'pending' || item.approvalStatus === 'rejected';
  const canDirectEdit    = item.contentStatus === 'draft' && !isLocked;

  const contentType  = cfg.contentType;
  const sectionLabel = cfg.chipLabel.charAt(0) + cfg.chipLabel.slice(1).toLowerCase();

  const viewApprovalRecord = item.approvalId ? {
    approvalId:       item.approvalId,
    actionType:       item.approvalActionType,
    contentType:      contentType,
    sectionLabel:     sectionLabel,
    contentName:      displayName,
    submittedByName: '',
    submittedAt:      null,
    status:            item.approvalStatus,
  } : null;

  const parentLockTooltip = parentLocked
    ? `The booth has a pending approval — resolve it first`
    : '';

  const editTooltip = parentLocked
    ? parentLockTooltip
    : isLocked
      ? item.approvalStatus === 'pending'
        ? 'A pending proposal exists — View it or Withdraw first'
        : 'A rejected proposal exists — View it or Dismiss first'
      : canDirectEdit
        ? `Edit ${sectionLabel}`
        : `Propose ${deleteEntityText} edit (requires approval)`;

  const deleteTooltip = parentLocked
    ? parentLockTooltip
    : isLocked
      ? item.approvalStatus === 'pending'
        ? 'A pending proposal exists — Withdraw it first'
        : 'A rejected proposal exists — Dismiss it first'
      : canDirectEdit
        ? `Delete ${sectionLabel}`
        : `Propose ${deleteEntityText} deletion (requires approval)`;

  const convertTooltip = parentLocked
    ? parentLockTooltip
    : isLocked
      ? item.approvalStatus === 'pending'
        ? 'A pending proposal exists — Withdraw it first'
        : 'A rejected proposal exists — Dismiss it first'
      : canDirectEdit
        ? `Convert this ${deleteEntityText} to another type (deletes this item, creates a draft item of the new type)`
        : `Convert this ${deleteEntityText} to another type (requires approval — deletes this item, creates a draft item of the new type)`;

  const handleEditClick = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
    onEdit?.(!canDirectEdit);
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
    if (canDirectEdit) setDeleteModalOpen(true);
    else setProposeDeleteOpen(true);
  };

  const handleConvertClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.blur();
    onConvert?.(item, itemType);
  };

  const confirmProposeDelete = async () => {
    try {
      const contentName = title || mediaTitle || `${sectionLabel} ${item.id}`;
      await submitApproval({
        contentType,
        contentId: item.id,
        contentName: contentName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteOpen(false);
      onSuccess?.('pending');
    } catch {
      setProposeDeleteOpen(false);
    }
  };

  const fallbackIcon = mediaOnly
    ? (MEDIA_ICONS[mediaTypeId] || MEDIA_ICONS[1])
    : isCustom
      ? <CalendarMonthIcon sx={{ fontSize: 38, color: "primary.lighter" }} />
      : <ImageIcon sx={{ fontSize: 38, color: "primary.lighter" }} />;

  return (
    <>
      <Card
        onMouseEnter={preview ? undefined : () => setHovered(true)}
        onMouseLeave={preview ? undefined : () => setHovered(false)}
        sx={{
          borderRadius: 3,
          bgcolor: "white",
          boxShadow: (!preview && hovered)
            ? "0 12px 32px rgba(10,50,109,0.2)"
            : "0 2px 10px rgba(10,50,109,0.1)",
          transform: (!preview && hovered) ? "translateY(-3px)" : "translateY(0)",
          transition: "all 0.22s ease",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Snapshot version banner */}
        {isSnapshot && (
          <Box sx={{
            bgcolor: item.approvalStatus === 'pending' ? '#d97706' : '#dc2626',
            color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
          }}>
            {item.approvalActionType === 'convert'
              ? (item.approvalStatus === 'pending' ? 'Convert — Pending Review' : 'Convert — Rejected')
              : (item.approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected')}
          </Box>
        )}

        {/* Top image / media area */}
        <Box sx={{ height: { xs: 110, sm: 120, md: 130 }, position: "relative", bgcolor: "grey.100", flexShrink: 0, overflow: "hidden" }}>
          {showThumb && !imageLoaded && !imageError && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ color: "primary.lighter" }} />
            </Box>
          )}

          {showThumb && (
            <Box
              component="img"
              src={previewSrc}
              alt={title || mediaTitle || "Media"}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: imageLoaded && !imageError ? "block" : "none" }}
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
            />
          )}

          {!showThumb && (
            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {fallbackIcon}
            </Box>
          )}

          <Chip
            label={<LocalText>{badgeLabel}</LocalText>}
            size="small"
            sx={{
              position: "absolute", top: 8, left: 8,
              bgcolor: cfg.chipColor,
              color: "white", fontSize: 10, fontWeight: 700, letterSpacing: 0.8, height: 20,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>

        {/* Body */}
        <CardContent sx={{ flex: 1, px: 1.5, py: 1, pb: "8px !important" }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main" noWrap title={displayName} sx={{ minWidth: 0 }}>
            {displayName || "—"}
          </Typography>

          {hostname && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.2 }}
              title={subtitleLink}
            >
              <LinkIcon sx={{ fontSize: 11 }} />
              {hostname}
            </Typography>
          )}
        </CardContent>

        {/* Approval status row */}
        {!preview && (isSnapshot ? item.approvalStatus : item.contentStatus) && (
          <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Badge: live cards use contentStatus; snapshot cards use approvalStatus */}
            {(isSnapshot ? item.approvalStatus : item.contentStatus) && (
              <ApprovalStatusBadge
                status={isSnapshot ? item.approvalStatus : item.contentStatus}
                actionType={item.approvalActionType}
              />
            )}

            {/* True draft only (no ca record): Submit for Approval */}
            {!isSnapshot && canDirectEdit && item.contentStatus === 'draft' && showActions && !parentLocked && (
              <SubmitForApprovalButton contentType={contentType} contentId={item.id} contentName={displayName} onSubmitted={() => onSuccess?.('pending')} />
            )}

            {/* Snapshot: View button */}
            {isSnapshot && viewApprovalRecord && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                onClick={() => setViewProposalOpen(true)}
                sx={viewBtnSx}
              >
                View
              </Button>
            )}
            {/* Snapshot pending: Withdraw */}
            {isSnapshot && item.approvalStatus === 'pending' && item.approvalId && (
              <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} />
            )}
            {/* Snapshot rejected: Dismiss + Edit & Resubmit */}
            {isSnapshot && item.approvalStatus === 'rejected' && item.approvalId && (
              <>
                <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
                {onEditRejected && (
                  <Button
                    size="small"
                    onClick={() => onEditRejected(null, item.id)}
                    sx={{
                      borderColor: '#6366f1', color: '#4338ca', bgcolor: '#eef2ff',
                      borderRadius: '8px', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
                      boxShadow: 'none', border: '1px solid',
                      '&:hover': { bgcolor: '#e0e7ff', borderColor: '#4f46e5', boxShadow: 'none' },
                    }}
                  >
                    Edit &amp; Resubmit
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

        {/* Footer — Convert (left) | Edit, Delete (right) */}
        {!preview && !isSnapshot && showActions && (
          <Box
            sx={{
              borderTop: "1px solid", borderColor: "grey.100",
              px: 1.5, py: 1,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
            }}
          >
            <Box>
              {onConvert && (
                <Tooltip title={convertTooltip}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<SwapHorizIcon sx={{ fontSize: 15 }} />}
                      disabled={isLocked || parentLocked}
                      onClick={handleConvertClick}
                      sx={{
                        color: '#0a326d', textTransform: 'none', fontSize: 12, fontWeight: 700,
                        minWidth: 0, px: 1, py: 0.4,
                        '&:hover': { bgcolor: '#eef2ff' },
                      }}
                    >
                      Convert
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isLocked ? (
                <Tooltip title="View live content (read-only)">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.blur(); onViewLive?.(); }}
                    sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={editTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={parentLocked}
                      onClick={parentLocked ? undefined : handleEditClick}
                      sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                    >
                      <EditIcon sx={{ fontSize: 15, color: '#0a326d' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={deleteTooltip}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isLocked || parentLocked}
                    onClick={handleDeleteClick}
                    sx={{ '&:hover': { bgcolor: '#fff0f0' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 15, color: '#d32f2f' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Card>

      {!preview && !isSnapshot && (
        <ConfirmModal
          open={deleteModalOpen}
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => { deletePortal(item.boothId, item.id, itemType); setDeleteModalOpen(false); }}
          severity="danger"
          title={deleteTitleText}
          message={`Are you sure you want to delete this ${deleteEntityText}?`}
          subtitle="This action cannot be undone."
          confirmLabel="Delete"
        />
      )}

      {!preview && !isSnapshot && (
        <ConfirmModal
          open={proposeDeleteOpen}
          onCancel={() => setProposeDeleteOpen(false)}
          onConfirm={confirmProposeDelete}
          severity="warning"
          title={`Propose Deletion`}
          message={`Submit a delete request for this ${deleteEntityText}? The content remains active until an approver approves.`}
          confirmLabel="Submit for Approval"
        />
      )}

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={onEditRejected ? (snap) => { setViewProposalOpen(false); onEditRejected(snap, item._liveId ?? item.id); } : undefined}
        />
      )}
    </>
  );
};

export default PortalCard;
